import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { RouteBadge } from "@/lib/routeTypes";
import { ClipboardPaste, Send, Settings2 } from "lucide-react";
import RulesDialog from "@/components/RulesDialog";

const EXAMPLE = `Wave:- 1
* Scar1518:- Abhishek ( Dodge 148)
* Scar1205:- Digvijay ( Transit 49754)
* Scar1290:- Navjot ( Transit 49780)
Wave:- 2
* Scar1524:- Sujal ( Vaug NV)
* Scar1295:- Nitiesh ( Gmc99962)
* Scar1841:- Vishavjeet ( EV)`;

export default function Paste() {
  const [text, setText] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const qc = useQueryClient();

  async function submit() {
    if (!text.trim()) {
      toast.error("Please paste some route lines first.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/paste", { text, date });
      setResult(data);
      toast.success(`Saved ${data.inserted} entries${data.errors?.length ? ` · ${data.errors.length} skipped` : ""}`);
      setText("");
      // Refresh every page's data
      qc.invalidateQueries();
    } catch (e) {
      toast.error("Failed to parse. " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-6 md:p-8 max-w-6xl">
      <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Ingest</div>
      <h1 className="font-heading text-4xl font-semibold tracking-tight mt-1">Paste Roster</h1>
      <p className="text-sm text-slate-500 mt-1">
        One assignment per line. Format: <span className="font-mono-data text-slate-800 bg-slate-100 px-1.5 py-0.5">RouteCode:- Driver Name ( VanCode )</span>
      </p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Date</label>
            <input
              data-testid="paste-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="mt-1 block px-3 py-2 border border-[#E2E2E8] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Route lines</label>
            <textarea
              data-testid="paste-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={EXAMPLE}
              className="mt-1 w-full min-h-[400px] px-4 py-3 border border-[#E2E2E8] rounded-md font-mono-data text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white shadow-inner"
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={submit}
              disabled={loading}
              data-testid="paste-submit"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-medium rounded-md transition-colors duration-150 disabled:opacity-60"
            >
              <Send className="w-4 h-4" /> {loading ? "Saving..." : "Parse & Save"}
            </button>
            <button
              onClick={() => setText(EXAMPLE)}
              data-testid="paste-example"
              className="inline-flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md transition-colors duration-150"
            >
              <ClipboardPaste className="w-4 h-4" /> Load example
            </button>
          </div>
        </div>

        <aside className="bg-white border border-[#E2E2E8] rounded-none md:rounded-lg p-5 h-fit sticky top-6">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Rules</div>
            <RulesDialog
              trigger={
                <button data-testid="open-rules-btn" className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-600 hover:text-[#2563EB] hover:bg-blue-50 rounded-md">
                  <Settings2 className="w-3.5 h-3.5" /> Edit
                </button>
              }
            />
          </div>
          <ul className="mt-3 space-y-2 text-sm text-slate-700">
            <li className="flex items-center gap-2"><RouteBadge type="small" /> starts with scar14 / scar15</li>
            <li className="flex items-center gap-2"><RouteBadge type="big" /> starts with scar12 / scar13 / scar18</li>
            <li className="flex items-center gap-2"><RouteBadge type="pickup" /> starts with scar5</li>
            <li className="flex items-center gap-2"><RouteBadge type="walmart" /> starts with scar9 or walmart</li>
          </ul>
          <div className="mt-6 text-xs text-slate-500">
            Every page updates automatically after you save. New drivers get their own tab in Drivers. Add or remove classification rules any time.
          </div>
        </aside>
      </div>

      {result && (
        <div className="mt-8 bg-white border border-[#E2E2E8] rounded-none md:rounded-lg overflow-hidden">
          <div className="px-5 py-4 border-b border-[#E2E2E8] flex items-center justify-between">
            <h2 className="font-heading text-lg font-medium">Last import</h2>
            <span className="text-xs text-slate-500 font-mono-data">
              {result.inserted} saved{result.errors?.length ? ` · ${result.errors.length} errors` : ""}
            </span>
          </div>
          {result.entries?.length > 0 && (
            <div>
              <div className="grid grid-cols-12 px-5 py-2 border-b border-[#E2E2E8] bg-slate-50 text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
                <div className="col-span-3">Route</div>
                <div className="col-span-4">Driver</div>
                <div className="col-span-3">Van</div>
                <div className="col-span-2">Type</div>
              </div>
              {result.entries.map((e) => (
                <div key={`${e.route_code}-${e.driver_name}-${e.van_code}`} className="grid grid-cols-12 px-5 py-2.5 border-b border-[#E2E2E8] last:border-b-0 items-center">
                  <div className="col-span-3 font-mono-data text-sm">{e.route_code}</div>
                  <div className="col-span-4 text-sm">{e.driver_name}</div>
                  <div className="col-span-3 font-mono-data text-sm text-slate-600">{e.van_code}</div>
                  <div className="col-span-2"><RouteBadge type={e.route_type} /></div>
                </div>
              ))}
            </div>
          )}
          {result.errors?.length > 0 && (
            <div className="p-4 bg-red-50 border-t border-red-100">
              <div className="text-xs uppercase tracking-[0.2em] text-red-700 font-semibold mb-2">Skipped ({result.errors.length})</div>
              <ul className="space-y-1 text-sm text-red-700 font-mono-data">
                {result.errors.map((err) => (
                  <li key={err.raw} className="truncate">· {err.raw}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
